import { ALL_CONFIGS } from '@/config';
import { ConfigLaunchPad } from '@/program/web3MemeLaunchpad';
import { ConfigLaunchPadDefai } from '@/program/web3DefaiLaunchpad';
import { BN } from '@coral-xyz/anchor';
import { PublicKey } from '@solana/web3.js';
import { create } from 'zustand';

export type ConfigState = {
  bondingCurveConfig: {
    waitingPeriodToClaim: BN;
    authority: PublicKey;
    pendingAuthority: PublicKey;
    teamWallet: PublicKey;
    migrator: PublicKey;
    communityPoolWallet: PublicKey;
    stakingPoolWallet: PublicKey;
    platformBuyFee: number;
    platformSellFee: number;
    platformMigrationFee: number;
    curveLimit: BN;
    lamportAmountConfig: BN;
    tokenSupplyConfig: BN;
    tokenDecimalsConfig: number;
    whitelistRoundPeriod: BN;
    waitingPeriod: BN;
    creatorFee: number;
    communityPoolFee: number;
    aiAgentFee: number;
    stakingFee: number;
    stakingProgram: PublicKey;
    maxTokenBuyInParty: BN;
    maxSolBuyInPublic: BN;
  };
  DefaiTokenConfig: ConfigLaunchPadDefai;
  agentCurveLimit: number;
  agentCurveInit: number;
  memeCurveLimit: number;
  memeCurveInit: number;
  memeBondingCurveConfig: ConfigLaunchPad;
  DEGENBondingCurveConfig: ConfigLaunchPad;
  DEGENCurveLimit: number;
  DEGENCurveInit: number;
  configSnapshot: {
    token: string;
    metadata: any;
  }[];
  listAllowAddress: string[];
  stakeConfig: {
    bump: number[];
    version: number;
    authority: PublicKey;
    stakeCurrencyMint: PublicKey;
    lockPeriod: number;
    lockExtendTime: number;
    softCap: BN;
    totalReward: BN;
  };
};

export type ConfigStateAction = {
  handleSetListAllowAddress: (
    listAllowAddress: ConfigState['listAllowAddress'],
  ) => void;
  handleSetBondingCurveConfig: (
    bondingCurveConfig: ConfigState['bondingCurveConfig'],
  ) => void;
  handleSetDefaiConfig: (
    bondingCurveConfig: ConfigState['DefaiTokenConfig'],
  ) => void;
  handleSetMemeCurveConfig: (
    memeBondingCurveConfig: ConfigState['memeBondingCurveConfig'],
  ) => void;
  handleSetStakeConfig: (stakeConfig: ConfigState['stakeConfig']) => void;
  handleSetAgentCurveLimit: (
    agentCurveLimit: ConfigState['agentCurveLimit'],
  ) => void;
  handleSetAgentInitCurve: (
    stakeagentCurveInitConfig: ConfigState['agentCurveInit'],
  ) => void;
  handleSetMemeCurveLimit: (
    memeCurveLimit: ConfigState['memeCurveLimit'],
  ) => void;
  handleSetMemeInitCurve: (memeCurveInit: ConfigState['memeCurveInit']) => void;
  handleSetDEGENCurveConfig: (
    DEGENBondingCurveConfig: ConfigState['DEGENBondingCurveConfig'],
  ) => void;
  handleSetDEGENCurveLimit: (
    DEGENCurveLimit: ConfigState['DEGENCurveLimit'],
  ) => void;
  handleSetDEGENInitCurve: (
    DEGENCurveLimit: ConfigState['DEGENCurveLimit'],
  ) => void;
  handleSetSnapshotConfig: (
    configSnapshot: ConfigState['configSnapshot'],
  ) => void;
  resetState: () => void;
};

const initialState: ConfigState = {
  agentCurveLimit: ALL_CONFIGS.BONDING_CURVE_LIMIT,
  agentCurveInit: ALL_CONFIGS.INIT_SOL_BONDING_CURVE,
  memeCurveLimit: ALL_CONFIGS.MEME_BONDING_CURVE_LIMIT,
  memeCurveInit: ALL_CONFIGS.MEME_INIT_SOL_BONDING_CURVE,
  bondingCurveConfig: null,
  DefaiTokenConfig: null,
  memeBondingCurveConfig: null,
  DEGENBondingCurveConfig: null,
  DEGENCurveLimit: ALL_CONFIGS.DEGEN_BONDING_CURVE_LIMIT,
  DEGENCurveInit: ALL_CONFIGS.DEGEN_INIT_SOL_BONDING_CURVE,
  listAllowAddress: [],
  stakeConfig: null,
  configSnapshot: [],
};

export type DepositStoreType = ConfigState & { actions: ConfigStateAction };

const useDetectionStore = create<DepositStoreType>()((set) => ({
  //States
  ...initialState,

  //Actions
  actions: {
    handleSetSnapshotConfig: (configSnapshot) => set({ configSnapshot }),
    handleSetStakeConfig: (stakeConfig) => set({ stakeConfig }),
    handleSetListAllowAddress: (listAllowAddress) => set({ listAllowAddress }),
    handleSetBondingCurveConfig: (bondingCurveConfig) =>
      set({ bondingCurveConfig }),
    handleSetDefaiConfig: (DefaiTokenConfig) => set({ DefaiTokenConfig }),
    handleSetAgentCurveLimit: (agentCurveLimit) => set({ agentCurveLimit }),
    handleSetAgentInitCurve: (agentCurveInit) => set({ agentCurveInit }),
    handleSetMemeCurveLimit: (memeCurveLimit) => set({ memeCurveLimit }),
    handleSetMemeInitCurve: (memeCurveInit) => set({ memeCurveInit }),
    handleSetMemeCurveConfig: (bondingCurveConfig) =>
      set({ memeBondingCurveConfig: bondingCurveConfig }),
    handleSetDEGENCurveLimit: (bondingCurveConfig) =>
      set({ DEGENCurveLimit: bondingCurveConfig }),
    handleSetDEGENInitCurve: (bondingCurveConfig) =>
      set({ DEGENCurveInit: bondingCurveConfig }),
    handleSetDEGENCurveConfig: (bondingCurveConfig) =>
      set({ DEGENBondingCurveConfig: bondingCurveConfig }),
    resetState: () =>
      set((state) => {
        state.bondingCurveConfig = initialState.bondingCurveConfig;
        state.stakeConfig = initialState.stakeConfig;
        state.listAllowAddress = initialState.listAllowAddress;

        return state;
      }),
  },
}));

export default useDetectionStore;
